

import { useState, useMemo } from "react";
import axios from "axios";

/**
 * Centered ReviewForm
 * - Centers inside the page
 * - Hover polish on card & fields
 * - Matches your Contact page gradient accents
 */
export default function ReviewForm({ onReviewAdded }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    rating: 0,
    comment: "",
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const maxComment = 500;

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email (or leave blank).";
    if (form.rating < 1 || form.rating > 5) e.rating = "Please select a star rating.";
    if (!form.comment.trim() || form.comment.trim().length < 10)
      e.comment = "Please write at least 10 characters.";
    if (form.comment.length > maxComment)
      e.comment = `Please keep your review under ${maxComment} characters.`;
    return e;
  }, [form, maxComment]);

  const hasErrors = Object.keys(errors).length > 0;
  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const onBlur = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, rating: true, comment: true });
    setStatus({ type: "", message: "" });
    if (hasErrors) return;

    try {
      setSubmitting(true);
      const { data } = await axios.post("http://localhost:5001/api/reviews", form); ///api/reviews
      onReviewAdded?.(data);
      setStatus({ type: "success", message: "Thanks! Your review was submitted." });
      setForm({ name: "", email: "", rating: 0, comment: "" });
      setTouched({});
    } catch (err) {
      console.error("Error adding review:", err);
      setStatus({
        type: "error",
        message: err?.response?.data?.message || "Sorry, something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStarKey = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setField("rating", Math.min(5, (form.rating || 0) + 1));
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setField("rating", Math.max(1, (form.rating || 0) - 1));
    }
  };

  return (
    <div className="w-full min-h-[60vh] px-4 py-10 flex items-center justify-center">
      <form
        onSubmit={handleSubmit}
        noValidate
        className="group relative w-full max-w-xl rounded-b-lg bg-white/90 shadow-xl ring-1 ring-gray-100 backdrop-blur
                   transition-all duration-300 hover:shadow-2xl hover:ring-gray-200 hover:-translate-y-0.5"
      >
        {/* gradient top border */}
        <div className="absolute inset-x-0 -top-[1px] h-[3px] rounded-t-2xl bg-gradient-to-r from-fuchsia-500 via-sky-500 to-amber-500" />

        {/* header */}
        <div className="px-5 pt-5">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-[11px] font-semibold text-gray-700 ring-1 ring-black/5">
            <span className="inline-block h-2 w-2 animate-ping rounded-full bg-emerald-500" />
            We appreciate your feedback
          </span>
          {/* <h3 className="mt-3 bg-gradient-to-r from-fuchsia-600 via-sky-600 to-amber-600 bg-clip-text text-lg font-extrabold tracking-tight text-transparent">
            Leave a Review
          </h3> */}
          {/* <p className="mt-1 text-sm text-gray-600">Help others by sharing your experience.</p> */}
        </div>

        {/* body */}
        <div className="px-5 pb-5 pt-4 space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-800">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              onBlur={() => onBlur("name")}
              className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition
                placeholder:text-gray-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100
                hover:border-sky-300 ${touched.name && errors.name ? "border-red-400 focus:ring-red-100" : "border-gray-200"}`}
              required
              autoComplete="name"
            />
            {touched.name && errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>

          {/* Email (optional) */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-800">
              Email <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              onBlur={() => onBlur("email")}
              className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition
                placeholder:text-gray-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100
                hover:border-sky-300 ${touched.email && errors.email ? "border-red-400 focus:ring-red-100" : "border-gray-200"}`}
              autoComplete="email"
              inputMode="email"
            />
            {touched.email && errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>

          {/* Rating */}
          <div>
            <span className="block text-sm font-medium text-gray-800">
              Rating <span className="text-red-500">*</span>
            </span>
            <div
              className="mt-1 inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white px-2 py-1
                         transition hover:border-sky-200"
              role="radiogroup"
              aria-label="Star rating"
              tabIndex={0}
              onKeyDown={handleStarKey}
              onBlur={() => onBlur("rating")}
            >
              {[1, 2, 3, 4, 5].map((star) => {
                const active = form.rating >= star;
                return (
                  <button
                    key={star}
                    type="button"
                    role="radio"
                    aria-checked={form.rating === star}
                    onClick={() => setField("rating", star)}
                    className={`text-2xl leading-none transition-transform
                      ${active ? "text-amber-400" : "text-gray-300 hover:text-amber-300"}
                      hover:scale-110 focus:outline-none`}
                    title={`${star} star${star > 1 ? "s" : ""}`}
                  >
                    ★
                  </button>
                );
              })}
            </div>
            {touched.rating && errors.rating && <p className="mt-1 text-xs text-red-600">{errors.rating}</p>}
          </div>

          {/* Comment */}
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="comment" className="text-sm font-medium text-gray-800">
                Review <span className="text-red-500">*</span>
              </label>
              <span className={`text-xs ${form.comment.length > maxComment ? "text-red-600" : "text-gray-500"}`}>
                {form.comment.length}/{maxComment}
              </span>
            </div>
            <textarea
              id="comment"
              placeholder="Share your experience (min 10 characters)"
              value={form.comment}
              onChange={(e) => setField("comment", e.target.value)}
              onBlur={() => onBlur("comment")}
              maxLength={maxComment + 50}
              className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition
                placeholder:text-gray-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 min-h-[110px] resize-y
                hover:border-sky-300 ${touched.comment && errors.comment ? "border-red-400 focus:ring-red-100" : "border-gray-200"}`}
              required
            />
            {touched.comment && errors.comment && <p className="mt-1 text-xs text-red-600">{errors.comment}</p>}
          </div>

          {/* Status */}
          {status.message && (
            <div
              className={`rounded-xl px-3 py-2 text-sm ${
                status.type === "success"
                  ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
                  : "bg-red-50 text-red-700 ring-1 ring-red-100"
              }`}
            >
              {status.message}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || hasErrors}
            className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition
              ${
                submitting || hasErrors
                  ? "bg-gray-400"
                  : "bg-gradient-to-r from-fuchsia-600 via-sky-600 to-amber-600 hover:brightness-110 hover:shadow-xl"
              }`}
            aria-disabled={submitting || hasErrors}
          >
            {submitting && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4A4 4 0 008 12H4z" />
              </svg>
            )}
            {submitting ? "Submitting..." : "Submit Review"}
          </button>

          {/* foot hint */}
          <p className="text-[11px] text-gray-500">
            By submitting, you agree to let us display your name, rating, and review on our site.
          </p>
        </div>

        {/* ambient hover halo */}
        <div className="pointer-events-none absolute -inset-2 -z-10 rounded-3xl bg-gradient-to-br from-fuchsia-400/20 via-sky-300/20 to-amber-300/20 opacity-0 blur-2xl transition duration-300 group-hover:opacity-100" />
      </form>
    </div>
  );
}



































// import { useState, useMemo } from "react";
// import axios from "axios";

// /**
//  * Professional, accessible review form
//  * - Clean card UI with subtle gradients
//  * - Star rating with hover + keyboard (← →)
//  * - Client-side validation + inline errors
//  * - Loading state, success + error feedback
//  * - Optional email (validated if provided)
//  * - Character counter for comment
//  */
// export default function ReviewForm({ onReviewAdded }) {
//   const [form, setForm] = useState({
//     name: "",
//     email: "",
//     rating: 0,
//     comment: "",
//   });
//   const [touched, setTouched] = useState({});
//   const [submitting, setSubmitting] = useState(false);
//   const [status, setStatus] = useState({ type: "", message: "" });

//   const maxComment = 500;

//   const errors = useMemo(() => {
//     const e = {};
//     if (!form.name.trim()) e.name = "Please enter your name.";
//     if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
//       e.email = "Please enter a valid email (or leave blank).";
//     if (form.rating < 1 || form.rating > 5)
//       e.rating = "Please select a star rating.";
//     if (!form.comment.trim() || form.comment.trim().length < 10)
//       e.comment = "Please write at least 10 characters.";
//     if (form.comment.length > maxComment)
//       e.comment = `Please keep your review under ${maxComment} characters.`;
//     return e;
//   }, [form, maxComment]);

//   const hasErrors = Object.keys(errors).length > 0;

//   const setField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//   };

//   const onBlur = (field) => setTouched((t) => ({ ...t, [field]: true }));

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setTouched({ name: true, email: true, rating: true, comment: true });
//     setStatus({ type: "", message: "" });

//     if (hasErrors) return;

//     try {
//       setSubmitting(true);
//       const { data } = await axios.post("/api/reviews", form);
//       onReviewAdded?.(data);
//       setStatus({ type: "success", message: "Thanks! Your review was submitted." });
//       setForm({ name: "", email: "", rating: 0, comment: "" });
//       setTouched({});
//     } catch (err) {
//       console.error("Error adding review:", err);
//       setStatus({
//         type: "error",
//         message:
//           err?.response?.data?.message ||
//           "Sorry, something went wrong. Please try again.",
//       });
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   // Star rating helpers
//   const handleStarKey = (e) => {
//     if (e.key === "ArrowRight") {
//       e.preventDefault();
//       setField("rating", Math.min(5, (form.rating || 0) + 1));
//     }
//     if (e.key === "ArrowLeft") {
//       e.preventDefault();
//       setField("rating", Math.max(1, (form.rating || 0) - 1));
//     }
//   };

//   return (
//     <form
//       onSubmit={handleSubmit}
//       className="relative w-full max-w-xl rounded-2xl bg-white shadow-xl ring-1 ring-gray-100"
//       noValidate
//     >
//       {/* gradient top border */}
//       <div className="absolute inset-x-0 -top-[1px] h-[3px] rounded-t-2xl bg-gradient-to-r from-indigo-500 via-sky-400 to-violet-500" />

//       {/* header */}
//       <div className="px-5 pt-5">
//         <h3 className="text-lg font-semibold text-gray-900">Leave a Review</h3>
//         <p className="mt-1 text-sm text-gray-600">
//           We value your feedback. Your review helps us improve.
//         </p>
//       </div>

//       {/* body */}
//       <div className="px-5 pb-5 pt-4 space-y-4">
//         {/* Name */}
//         <div>
//           <label htmlFor="name" className="block text-sm font-medium text-gray-800">
//             Name <span className="text-red-500">*</span>
//           </label>
//           <input
//             id="name"
//             type="text"
//             placeholder="Your name"
//             value={form.name}
//             onChange={(e) => setField("name", e.target.value)}
//             onBlur={() => onBlur("name")}
//             className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition
//               placeholder:text-gray-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100
//               ${touched.name && errors.name ? "border-red-400 focus:ring-red-100" : "border-gray-200"}`}
//             required
//             autoComplete="name"
//           />
//           {touched.name && errors.name && (
//             <p className="mt-1 text-xs text-red-600">{errors.name}</p>
//           )}
//         </div>

//         {/* Email (optional) */}
//         <div>
//           <label htmlFor="email" className="block text-sm font-medium text-gray-800">
//             Email <span className="text-gray-400">(optional)</span>
//           </label>
//           <input
//             id="email"
//             type="email"
//             placeholder="you@example.com"
//             value={form.email}
//             onChange={(e) => setField("email", e.target.value)}
//             onBlur={() => onBlur("email")}
//             className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition
//               placeholder:text-gray-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100
//               ${touched.email && errors.email ? "border-red-400 focus:ring-red-100" : "border-gray-200"}`}
//             autoComplete="email"
//             inputMode="email"
//           />
//           {touched.email && errors.email && (
//             <p className="mt-1 text-xs text-red-600">{errors.email}</p>
//           )}
//         </div>

//         {/* Rating */}
//         <div>
//           <span className="block text-sm font-medium text-gray-800">
//             Rating <span className="text-red-500">*</span>
//           </span>
//           <div
//             className="mt-1 inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-2 py-1"
//             role="radiogroup"
//             aria-label="Star rating"
//             tabIndex={0}
//             onKeyDown={handleStarKey}
//             onBlur={() => onBlur("rating")}
//           >
//             {[1, 2, 3, 4, 5].map((star) => {
//               const active = form.rating >= star;
//               return (
//                 <button
//                   key={star}
//                   type="button"
//                   role="radio"
//                   aria-checked={form.rating === star}
//                   onClick={() => setField("rating", star)}
//                   className={`text-2xl leading-none transition-transform
//                     ${active ? "text-amber-400" : "text-gray-300 hover:text-amber-300"}
//                     hover:scale-110 focus:outline-none`}
//                 >
//                   ★
//                 </button>
//               );
//             })}
//           </div>
//           {touched.rating && errors.rating && (
//             <p className="mt-1 text-xs text-red-600">{errors.rating}</p>
//           )}
//         </div>

//         {/* Comment */}
//         <div>
//           <div className="flex items-center justify-between">
//             <label htmlFor="comment" className="text-sm font-medium text-gray-800">
//               Review <span className="text-red-500">*</span>
//             </label>
//             <span
//               className={`text-xs ${
//                 form.comment.length > maxComment ? "text-red-600" : "text-gray-500"
//               }`}
//             >
//               {form.comment.length}/{maxComment}
//             </span>
//           </div>
//           <textarea
//             id="comment"
//             placeholder="Share your experience (min 10 characters)"
//             value={form.comment}
//             onChange={(e) => setField("comment", e.target.value)}
//             onBlur={() => onBlur("comment")}
//             maxLength={maxComment + 50} // soft guard
//             className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition
//               placeholder:text-gray-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 min-h-[110px] resize-y
//               ${touched.comment && errors.comment ? "border-red-400 focus:ring-red-100" : "border-gray-200"}`}
//             required
//           />
//           {touched.comment && errors.comment && (
//             <p className="mt-1 text-xs text-red-600">{errors.comment}</p>
//           )}
//         </div>

//         {/* Status */}
//         {status.message && (
//           <div
//             className={`rounded-xl px-3 py-2 text-sm ${
//               status.type === "success"
//                 ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
//                 : "bg-red-50 text-red-700 ring-1 ring-red-100"
//             }`}
//           >
//             {status.message}
//           </div>
//         )}

//         {/* Submit */}
//         <button
//           type="submit"
//           disabled={submitting || hasErrors}
//           className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition
//             ${submitting || hasErrors ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"} `}
//           aria-disabled={submitting || hasErrors}
//         >
//           {submitting && (
//             <svg
//               className="h-4 w-4 animate-spin"
//               viewBox="0 0 24 24"
//               fill="none"
//               aria-hidden="true"
//             >
//               <circle
//                 className="opacity-25"
//                 cx="12"
//                 cy="12"
//                 r="10"
//                 stroke="currentColor"
//                 strokeWidth="4"
//               />
//               <path
//                 className="opacity-75"
//                 fill="currentColor"
//                 d="M4 12a8 8 0 018-8v4A4 4 0 008 12H4z"
//               />
//             </svg>
//           )}
//           {submitting ? "Submitting..." : "Submit Review"}
//         </button>

//         {/* foot hint */}
//         <p className="text-[11px] text-gray-500">
//           By submitting, you agree to let us display your name, rating, and review on our site.
//         </p>
//       </div>
//     </form>
//   );
// }
